// src/views/AdminDashboard.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Card from "../../components/Cards/Card";
import Chart from "react-apexcharts";
import toast from "react-hot-toast";
import { IoHourglassOutline, IoSchoolOutline } from "react-icons/io5";
import { GrUserAdmin } from "react-icons/gr";
import { BsCalendar4Event } from "react-icons/bs";
import { Button, Select, Spin } from "antd";
import { getDashboardData } from "../../services/dashboardService";
import { getSessions } from "../../services/sessionService";
import EventsCalendar from "../../components/Calendar/EventsCalendar";
import { INSTITUTIONS_PATH } from "../../router/Admin/AdminRoutes";
import { Link } from "react-router-dom";

const { Option } = Select;

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [applying, setApplying] = useState(false);

  // Safe number formatter
  const fmt = useCallback(
    (n) => (typeof n === "number" ? n.toLocaleString() : "0"),
    []
  );

  useEffect(() => {
    loadSessions();
    loadDashboardData(); // initial
  }, []);

  const loadSessions = async () => {
    try {
      const { data } = await getSessions();
      setSessions(Array.isArray(data?.data) ? data.data : []);
    } catch (error) {
      // Quiet fail—UI still works without sessions
      // toast.error("Failed to load sessions", { id: "sessions" });
    }
  };

  const loadDashboardData = async (sessionId = null) => {
    setLoading(true);
    try {
      const { data } = await getDashboardData(sessionId);
      setDashboardData(data || {});
    } catch (error) {
      toast.error("Failed to load dashboard data", { id: "dashboard" });
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = async () => {
    setApplying(true);
    try {
      await loadDashboardData(selectedSession);
      toast.success("Filter applied", { id: "filter" });
    } finally {
      setApplying(false);
    }
  };

  const clearFilter = async () => {
    setSelectedSession(null);
    setApplying(true);
    try {
      await loadDashboardData(null);
      toast("Filter cleared", { id: "filter-cleared" });
    } finally {
      setApplying(false);
    }
  };

  // Cards
  const cardsItems = [
    {
      id: 2,
      label: "Total Institutions",
      icon: <IoSchoolOutline size={28} />,
      number: dashboardData?.institution_count ?? 0,
      bgClass: "bg-green-600/80",
      textClass: "text-white",
    },
    {
      id: 1,
      label: "New Institutions This Month",
      icon: <IoHourglassOutline size={28} />,
      number: dashboardData?.new_institutions_this_month ?? 0,
      bgClass: "bg-blue-500/80",
      textClass: "text-white",
    },
    {
      id: 3,
      label: "Institution Administrators",
      icon: <GrUserAdmin size={28} />,
      number: dashboardData?.institution_admin_count ?? 0,
      bgClass: "bg-yellow-500/90",
      textClass: "text-white",
    },
    {
      id: 4,
      label: "Upcoming Events",
      icon: <BsCalendar4Event size={28} />,
      number: dashboardData?.upcoming_events_count ?? 0,
      bgClass: "bg-red-500/70",
      textClass: "text-white",
    },
  ];

  // Top institutions (bar)
  const topInstitutions =
    dashboardData?.top_institutions_by_student_count ?? [];
  const institutionNames = topInstitutions.map((i) => i.institution_name);
  const studentCounts = topInstitutions.map((i) =>
    Number(i.total_students ?? 0)
  );

  const topInstitutionsChartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        animations: { easing: "easeinout", speed: 400 },
      },
      plotOptions: {
        bar: { horizontal: true, borderRadius: 4, barHeight: "60%" },
      },
      dataLabels: {
        enabled: true,
        offsetX: 12,
        style: { fontSize: "11px", colors: ["#f1f5f9"] }, // gray-800
        formatter: (val) => fmt(val),
      },
      xaxis: {
        categories: institutionNames,
        labels: { style: { fontSize: "12px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: { fontSize: "12px" } } },
      grid: { borderColor: "#f1f5f9" }, // slate-100
      colors: ["#60a5fa"], // blue-400
      tooltip: {
        y: { formatter: (val) => `${fmt(val)} students` },
      },
      responsive: [
        { breakpoint: 768, options: { dataLabels: { enabled: false } } }, // hide labels on small screens
      ],
    }),
    [institutionNames, fmt]
  );

  const topInstitutionsChartSeries = useMemo(
    () => [{ name: "Students", data: studentCounts }],
    [studentCounts]
  );

  // Institution categories (pie)
  const categoryDist = dashboardData?.institution_category_distribution ?? {};
  const institutionCategories = Object.keys(categoryDist);
  const institutionCategoryCounts = Object.values(categoryDist).map((v) =>
    Number(v ?? 0)
  );

  const institutionCategoryChartOptions = useMemo(
    () => ({
      chart: { type: "pie", toolbar: { show: false } },
      labels: institutionCategories,
      legend: { position: "bottom", fontSize: "12px" },
      colors: ["#60a5fa", "#fbbf24", "#34d399", "#f87171", "#a78bfa"],
      tooltip: { y: { formatter: (val) => `${fmt(val)} institutions` } },
      stroke: { width: 2, colors: ["#fff"] },
    }),
    [institutionCategories, fmt]
  );

  const institutionCategoryChartSeries = institutionCategoryCounts;

  // Gender ratio (stacked bar)
  const genderChartSeries = useMemo(
    () => [
      {
        name: "Male",
        data: [Number(dashboardData?.gender_distribution?.total_males ?? 0)],
      },
      {
        name: "Female",
        data: [Number(dashboardData?.gender_distribution?.total_females ?? 0)],
      },
    ],
    [dashboardData?.gender_distribution]
  );

  const genderChartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
        animations: { easing: "easeinout", speed: 400 },
      },
      plotOptions: { bar: { horizontal: true, borderRadius: 6 } },
      xaxis: {
        categories: ["Gender Distribution"],
        labels: { style: { fontSize: "12px" } },
      },
      yaxis: { title: { text: "Number of Students" } },
      colors: ["#60a5fa", "#fbbf24"],
      legend: {
        position: "bottom",
        labels: { colors: "#4b5563" },
        itemMargin: { horizontal: 8 },
      },
      grid: { borderColor: "#f1f5f9" },
      tooltip: { y: { formatter: (val) => `${fmt(val)} students` } },
    }),
    [fmt]
  );

  // Session growth (bar)
  const sessionGrowth = dashboardData?.student_growth_by_session ?? [];
  const sessionLabels = sessionGrowth.map((s) => s.session);
  const sessionValues = sessionGrowth.map((s) => Number(s.total_students ?? 0));

  const sessionGrowthBarOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
        animations: { easing: "easeinout", speed: 400 },
      },
      xaxis: {
        categories: sessionLabels,
        title: { text: "Year" },
        labels: { rotate: -45, style: { fontSize: "12px" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { title: { text: "Total Students" }, labels: { formatter: fmt } },
      plotOptions: { bar: { borderRadius: 4 } },
      grid: { borderColor: "#f1f5f9" },
      colors: ["#34d399"], // emerald-400
      tooltip: { y: { formatter: (val) => `${fmt(val)} students` } },
    }),
    [sessionLabels, fmt]
  );

  const sessionGrowthBarSeries = useMemo(
    () => [{ name: "Total Students", data: sessionValues }],
    [sessionValues]
  );

  const upcomingEvents = dashboardData?.upcoming_events ?? [];
  const recentInstitutions = dashboardData?.recent_institutions ?? [];

  return (
    <>
      <PageHeading subtitle1="Dashboard" />

      <Spin spinning={loading} tip="Loading dashboard...">
        <div className="px-6 md:px-8 py-6 md:py-8 flex flex-col w-full gap-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {cardsItems.map((item) => (
              <Card item={item} key={item.id} />
            ))}
          </div>

          {/* Events + Category */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="col-span-2 rounded-xl bg-white border border-black/5 shadow-sm px-5 py-5">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-800 mb-4">
                  Upcoming Events
                </h2>
              </div>
              <EventsCalendar eventsData={upcomingEvents} />
              {upcomingEvents.length === 0 && (
                <p className="mt-3 text-sm text-gray-500">
                  No upcoming events.
                </p>
              )}
            </div>

            <div className="rounded-xl bg-white border border-black/5 shadow-sm px-5 py-5">
              <h2 className="font-semibold text-gray-800 mb-4">
                Institutions by Category
              </h2>
              {institutionCategoryChartSeries.length > 0 ? (
                <Chart
                  options={institutionCategoryChartOptions}
                  series={institutionCategoryChartSeries}
                  type="pie"
                  height={300}
                  width="100%"
                />
              ) : (
                <p className="text-sm text-gray-500 mt-2">
                  No category data available.
                </p>
              )}
            </div>
          </div>

          {/* Recent + Growth */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-xl bg-white border border-black/5 shadow-sm px-5 py-5">
              <div className="flex items-center justify-between">
                <h2 className="font-semibold text-gray-800">
                  Recently Added Institutions
                </h2>
                <Link
                  className="text-sm text-primary-dark hover:opacity-90"
                  to={INSTITUTIONS_PATH}
                >
                  View all
                </Link>
              </div>

              {recentInstitutions.length > 0 ? (
                <ul className="mt-4 space-y-3">
                  {recentInstitutions.map((inst) => (
                    <li
                      key={inst.id}
                      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
                    >
                      <div className="font-medium text-gray-900">
                        {inst.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        Added on{" "}
                        {new Date(inst.created_at).toLocaleDateString()}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-gray-500">
                  No institutions added this month.
                </p>
              )}
            </div>

            <div className="rounded-xl bg-white border border-black/5 shadow-sm px-5 py-5">
              <h2 className="font-semibold text-gray-800 mb-2">
                Overall Student Growth by Academic Year
              </h2>
              {sessionLabels.length > 0 ? (
                <Chart
                  options={sessionGrowthBarOptions}
                  series={sessionGrowthBarSeries}
                  type="bar"
                  height={320}
                />
              ) : (
                <p className="text-sm text-gray-500">
                  No year-based data available.
                </p>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="rounded-xl bg-white border border-black/5 shadow-sm px-5 py-5">
            <label className="block font-medium text-gray-800 mb-3">
              Filter By Year
            </label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <Select
                placeholder="Select year"
                size="middle"
                value={selectedSession}
                onChange={(value) => setSelectedSession(value)}
                allowClear
                showSearch
                optionFilterProp="children"
                style={{ width: 260 }}
              >
                {sessions.map((session) => (
                  <Option key={session.id} value={session.id}>
                    {session.name}
                  </Option>
                ))}
              </Select>

              <div className="flex gap-2">
                <Button
                  type="primary"
                  size="middle"
                  loading={applying}
                  className="bg-primary! disabled:text-white! disabled:bg-primary/40! disabled:cursor-not-allowed"
                  onClick={applyFilter}
                  disabled={!selectedSession}
                >
                  Apply
                </Button>
                <Button
                  onClick={clearFilter}
                  disabled={applying || selectedSession === null}
                >
                  Reset
                </Button>
              </div>
            </div>
          </div>

          {/* Top 5 + Gender */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-xl bg-white border border-black/5 shadow-sm px-5 py-5">
              <h2 className="font-semibold text-gray-800 mb-2">
                Top 5 Institutions by Student Population
              </h2>
              {institutionNames.length > 0 ? (
                <Chart
                  options={topInstitutionsChartOptions}
                  series={topInstitutionsChartSeries}
                  type="bar"
                  height={340}
                />
              ) : (
                <p className="text-sm text-gray-500 mt-2">
                  No student data available.
                </p>
              )}
            </div>

            <div className="rounded-xl bg-white border border-black/5 shadow-sm px-5 py-5">
              <h2 className="font-semibold text-gray-800 mb-2">
                Student Gender Distribution
              </h2>
              {genderChartSeries.some((s) =>
                Array.isArray(s.data) ? s.data[0] > 0 : false
              ) ? (
                <Chart
                  options={genderChartOptions}
                  series={genderChartSeries}
                  type="bar"
                  height={320}
                />
              ) : (
                <p className="text-sm text-gray-500 mt-2">
                  No gender data available.
                </p>
              )}
            </div>
          </div>
        </div>
      </Spin>
    </>
  );
};

export default AdminDashboard;
